import React, { Component } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import { UDV_SURFACE, udvDialogProps } from './udvSurface'
import { t } from '../i18n'
import {
  loadRelatedStudiesPrefetchPref,
  saveRelatedStudiesPrefetchPref,
} from '../utils/relatedStudiesPrefetchPref'
import { formatStudyStatsLine } from '../utils/comparePriorStudy'

function formatDate(dcm) {
  const s = String(dcm || '')
  if (s.length >= 8) {
    return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`
  }
  return s || '—'
}

/**
 * Stats de un ítem: ticket → loaded files → catálogo local.
 * @param {object} item
 * @param {{ localByUid?: Object, loadedStatsByUid?: Object }} props
 * @returns {string}
 */
function resolveStatsLine(item, props) {
  if (!item) return ''
  const uid = item.studyInstanceUid ? String(item.studyInstanceUid) : ''
  const loaded =
    uid && props.loadedStatsByUid && props.loadedStatsByUid[uid]
      ? props.loadedStatsByUid[uid]
      : null
  if (loaded) return formatStudyStatsLine(loaded)

  const seriesFromTicket = Array.isArray(item.seriesImageCounts)
    ? item.seriesImageCounts.map((c) => ({ count: c }))
    : null
  const fromTicket = formatStudyStatsLine({
    seriesCount: item.seriesCount,
    instanceCount: item.instanceCount,
    series: seriesFromTicket,
  })
  if (fromTicket) return fromTicket

  const local =
    uid && props.localByUid && props.localByUid[uid]
      ? props.localByUid[uid]
      : null
  if (local) {
    return formatStudyStatsLine({
      seriesCount: local.seriesCount,
      instanceCount: local.instanceCount,
    })
  }
  return ''
}

/**
 * Historial de estudios del paciente (related_studies del ticket).
 * Cargar = merge al Explorer (sin layout). Abrir solo = reemplazo.
 * Comparar = toggle global en tools (no por fila).
 * @see docs/MANUAL-0.20.80-COMPARE-PRIORS.md
 *
 * Props:
 *   open, onClose,
 *   studies: normalizeRelatedStudyItem[],
 *   patientId?: string|null,
 *   currentTicketId?: string|null,
 *   loadedTicketIds?: string[] — tickets ya mergeados en el pane
 *   localByUid?: { [studyUid]: { instanceCount?, seriesCount? } }
 *   loadedStatsByUid?: { [studyUid]: ReturnType<studySeriesStats> }
 *   onLoadRelated: (ticketId: string) => void  — merge
 *   onOpenRelated: (ticketId: string) => void  — replace
 */
class PatientRelatedStudiesDlg extends Component {
  constructor(props) {
    super(props)
    this.state = {
      prefetchEnabled: loadRelatedStudiesPrefetchPref(),
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({ prefetchEnabled: loadRelatedStudiesPrefetchPref() })
    }
  }

  handlePrefetchToggle = (_e, checked) => {
    saveRelatedStudiesPrefetchPref(!!checked)
    this.setState({ prefetchEnabled: !!checked })
  }

  isCurrentItem = (item) => {
    if (!item) return false
    if (item.isCurrent) return true
    const cur = this.props.currentTicketId
    return !!(cur && item.ticketId === String(cur))
  }

  isInPane = (item) => {
    if (!item || !item.ticketId) return false
    if (this.isCurrentItem(item)) return true
    const loaded = this.props.loadedTicketIds
    if (!Array.isArray(loaded)) return false
    return loaded.map(String).includes(String(item.ticketId))
  }

  isInLocal = (item) => {
    if (!item || !item.studyInstanceUid) return false
    const map = this.props.localByUid
    if (!map || typeof map !== 'object') return false
    return !!map[String(item.studyInstanceUid)]
  }

  handleLoad = (item) => {
    if (!item || !item.ticketId || this.isCurrentItem(item)) return
    if (typeof this.props.onLoadRelated === 'function') {
      this.props.onLoadRelated(item.ticketId)
    }
  }

  handleOpenOnly = (item) => {
    if (!item || !item.ticketId) return
    if (typeof this.props.onOpenRelated === 'function') {
      this.props.onOpenRelated(item.ticketId)
    }
  }

  renderPrimary(item) {
    const date = formatDate(item.studyDate)
    const mod = item.modality || '—'
    const desc = item.description || t('relatedStudies.noDescription')
    const stats = resolveStatsLine(item, this.props)
    const tags = []
    if (this.isCurrentItem(item)) tags.push(t('relatedStudies.current'))
    else if (this.isInPane(item)) tags.push(t('relatedStudies.inPane'))
    if (this.isInLocal(item)) tags.push(t('relatedStudies.inLocal'))
    const mid = stats ? ` · ${stats}` : ''
    const suffix = tags.length ? ` · ${tags.join(' · ')}` : ''
    return `${date} · ${mod} · ${desc}${mid}${suffix}`
  }

  renderSecondary(item) {
    const parts = []
    if (item.clinicName) parts.push(item.clinicName)
    if (item.patientName) parts.push(item.patientName)
    return parts.length ? parts.join(' · ') : null
  }

  render() {
    const { open, onClose, studies, patientId } = this.props
    const list = Array.isArray(studies) ? studies : []
    const { prefetchEnabled } = this.state
    const canLoad = typeof this.props.onLoadRelated === 'function'

    return (
      <Dialog
        open={!!open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        {...udvDialogProps}
      >
        <DialogTitle style={UDV_SURFACE.title}>
          {t('relatedStudies.title')}
        </DialogTitle>
        <DialogContent style={UDV_SURFACE.content}>
          {patientId ? (
            <Typography
              variant="body2"
              style={{ ...UDV_SURFACE.muted, marginBottom: 8 }}
            >
              {t('relatedStudies.patientId', { id: patientId })}
            </Typography>
          ) : null}

          <FormControlLabel
            control={
              <Switch
                checked={prefetchEnabled}
                onChange={this.handlePrefetchToggle}
                color="primary"
              />
            }
            label={t('relatedStudies.prefetchToggle')}
            style={{ marginBottom: 4 }}
          />
          <Typography
            variant="caption"
            display="block"
            style={{ ...UDV_SURFACE.muted, marginBottom: 12 }}
          >
            {t('relatedStudies.prefetchHint')}
          </Typography>

          {list.length === 0 ? (
            <Typography variant="body2" style={UDV_SURFACE.muted}>
              {t('relatedStudies.empty')}
            </Typography>
          ) : (
            <List dense>
              {list.map((item) => {
                const isCurrent = this.isCurrentItem(item)
                const inPane = this.isInPane(item)
                return (
                  <ListItem
                    key={item.ticketId}
                    selected={!!isCurrent || !!inPane}
                    style={{ paddingRight: 200 }}
                  >
                    <ListItemText
                      primary={this.renderPrimary(item)}
                      secondary={this.renderSecondary(item)}
                      primaryTypographyProps={{
                        style: { color: 'inherit' },
                      }}
                      secondaryTypographyProps={{
                        style: { color: 'inherit', opacity: 0.75 },
                      }}
                    />
                    <ListItemSecondaryAction>
                      {!isCurrent ? (
                        <>
                          {canLoad && !inPane ? (
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => this.handleLoad(item)}
                              style={{ marginRight: 4, textTransform: 'none' }}
                            >
                              {t('relatedStudies.load')}
                            </Button>
                          ) : null}
                          <Button
                            size="small"
                            onClick={() => this.handleOpenOnly(item)}
                            style={{ textTransform: 'none' }}
                          >
                            {t('relatedStudies.openOnly')}
                          </Button>
                        </>
                      ) : (
                        <Typography
                          variant="caption"
                          style={UDV_SURFACE.muted}
                        >
                          {t('relatedStudies.current')}
                        </Typography>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                )
              })}
            </List>
          )}
        </DialogContent>
        <DialogActions style={UDV_SURFACE.actions}>
          <Button onClick={onClose} style={UDV_SURFACE.btnGhost}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

export default PatientRelatedStudiesDlg
